import { getPharmacySettings } from "@/core/admin/establishment-store";
import { apiListPharmacyInvoices, apiSyncPharmacyInvoice } from "@/core/hospitol-api-client";
import {
  markPharmacySotMigratedIfRemoteConfirmed,
  probeHospitolDurableConfigured,
} from "@/core/pharmacy/pharmacy-durable-sot";
import type { ProductStockState, ValidatedInvoice } from "./types";
import { readJson, writeJson, getStorageItem, setStorageItem } from "@/core/tenant/hospitol-storage";

const PRICES_KEY = "pharmacy.prices";
const RESERVATIONS_KEY = "pharmacy.reservations";
const INVOICES_KEY = "pharmacy.invoices";
const INVOICE_SEQ_KEY = "pharmacy.invoiceSeq";
const DRAFT_ID_KEY = "pharmacy.draftId";

export function getDraftId(): string {
  let id = getStorageItem(DRAFT_ID_KEY);
  if (!id) {
    id = `draft-${Date.now()}`;
    setStorageItem(DRAFT_ID_KEY, id);
  }
  return id;
}

export function resetDraftId(): string {
  const id = `draft-${Date.now()}`;
  setStorageItem(DRAFT_ID_KEY, id);
  return id;
}

export function getProductPrice(productId: string): number | null {
  const prices = readJson<Record<string, number>>(PRICES_KEY, {});
  return prices[productId] ?? null;
}

export function getAllProductPrices(): Record<string, number> {
  return readJson<Record<string, number>>(PRICES_KEY, {});
}

export function setProductPrice(productId: string, price: number): void {
  const prices = readJson<Record<string, number>>(PRICES_KEY, {});
  prices[productId] = price;
  writeJson(PRICES_KEY, prices);
}

export function clearProductPrice(productId: string): void {
  const prices = readJson<Record<string, number>>(PRICES_KEY, {});
  delete prices[productId];
  writeJson(PRICES_KEY, prices);
}

export function getReservations(): Record<string, number> {
  return readJson<Record<string, number>>(RESERVATIONS_KEY, {});
}

export function reserveProduct(productId: string, qty: number): void {
  const reservations = getReservations();
  reservations[productId] = (reservations[productId] ?? 0) + qty;
  writeJson(RESERVATIONS_KEY, reservations);
}

export function unreserveProduct(productId: string, qty: number): void {
  const reservations = getReservations();
  const next = Math.max(0, (reservations[productId] ?? 0) - qty);
  if (next === 0) delete reservations[productId];
  else reservations[productId] = next;
  writeJson(RESERVATIONS_KEY, reservations);
}

export function clearReservationsForLines(
  lines: Array<{ productId: string; quantity: number }>,
): void {
  for (const line of lines) {
    unreserveProduct(line.productId, line.quantity);
  }
}

export function getProductStockState(productId: string): ProductStockState {
  const reserved = getReservations()[productId] ?? 0;
  return {
    productId,
    reservedQty: reserved,
    status: reserved > 0 ? "reserved" : "available",
  };
}

export function nextInvoiceNumber(): string {
  const { invoicePrefix } = getPharmacySettings();
  const seq = Number(getStorageItem(INVOICE_SEQ_KEY) ?? "0") + 1;
  setStorageItem(INVOICE_SEQ_KEY, String(seq));
  const prefix = invoicePrefix.trim().toUpperCase() || "FAC";
  return `${prefix}-${String(seq).padStart(5, "0")}`;
}

export function listValidatedInvoices(): ValidatedInvoice[] {
  return readJson<ValidatedInvoice[]>(INVOICES_KEY, []);
}

/** Pull pharmacy invoices from API — durable SoT when configured (WP-H2). */
export async function syncPharmacyInvoicesFromApi(): Promise<boolean> {
  const remote = await apiListPharmacyInvoices();
  if (remote === null) return false;

  const durable = await probeHospitolDurableConfigured();

  if (remote.length > 0) {
    writeJson(INVOICES_KEY, remote);
    markPharmacySotMigratedIfRemoteConfirmed("invoices", durable, remote.length);
    return true;
  }

  const local = listValidatedInvoices();
  if (local.length > 0) {
    // WP-H2.1: never silent-discard local invoices when remote empty — push then keep LS if still empty.
    for (const invoice of local) {
      await apiSyncPharmacyInvoice(invoice);
    }
    const again = await apiListPharmacyInvoices();
    if (again === null) return false;
    if (again.length > 0) {
      writeJson(INVOICES_KEY, again);
      markPharmacySotMigratedIfRemoteConfirmed("invoices", durable, again.length);
    }
    return true;
  }

  return true;
}

export function saveValidatedInvoice(invoice: ValidatedInvoice): void {
  const list = listValidatedInvoices();
  list.unshift(invoice);
  writeJson(INVOICES_KEY, list);
  void apiSyncPharmacyInvoice(invoice);
}

export function updateValidatedInvoice(
  invoiceId: string,
  patch: Partial<ValidatedInvoice>,
): ValidatedInvoice | null {
  const list = listValidatedInvoices();
  const index = list.findIndex((inv) => inv.id === invoiceId);
  if (index < 0) return null;
  const updated = { ...list[index]!, ...patch };
  list[index] = updated;
  writeJson(INVOICES_KEY, list);
  void apiSyncPharmacyInvoice(updated);
  return updated;
}

export function searchValidatedInvoices(query: string): ValidatedInvoice[] {
  const q = query.trim().toLowerCase();
  if (!q) return listValidatedInvoices();

  return listValidatedInvoices().filter((inv) => {
    if (inv.number.toLowerCase().includes(q)) return true;
    if (inv.customer.name.toLowerCase().includes(q)) return true;
    if (inv.customer.phone.toLowerCase().includes(q)) return true;
    if (inv.lines.some((line) => line.label.toLowerCase().includes(q))) return true;
    return false;
  });
}

export function downloadInvoicesCsv(): void {
  const invoices = listValidatedInvoices();
  const header = ["Numero", "Date", "Client", "Telephone", "Montant", "Lignes"];
  const rows = invoices.map((inv) => [
    inv.number,
    new Date(inv.validatedAt).toISOString(),
    inv.customer.name,
    inv.customer.phone,
    String(inv.subtotal),
    String(inv.lines.length),
  ]);

  const csv = [header, ...rows]
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(";"))
    .join("\n");

  const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `factures-pharmacie-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

export function getDashboardStats(): {
  revenueToday: number;
  revenueTotal: number;
  invoiceCount: number;
  reservedProducts: number;
} {
  const invoices = listValidatedInvoices();
  const reservations = getReservations();
  const today = new Date().toDateString();

  let revenueToday = 0;
  let revenueTotal = 0;
  for (const inv of invoices) {
    revenueTotal += inv.subtotal;
    if (new Date(inv.validatedAt).toDateString() === today) {
      revenueToday += inv.subtotal;
    }
  }

  return {
    revenueToday,
    revenueTotal,
    invoiceCount: invoices.length,
    reservedProducts: Object.keys(reservations).length,
  };
}
